import api from './api';

class FinancialValuationService {
  // Get all financial valuation records with pagination and filtering
  static async getFinancialValuations(params = {}) {
    try {
      const response = await api.get('/financial-valuations', { params });
      // API returns { success: true, data: { valuations: [...], pagination: {...} } }
      // axios already extracts response.data, so we return it directly
      return response.data;
    } catch (error) {
      console.error('Error fetching financial valuations:', error);
      console.error('Error response:', error.response?.data);
      throw error.response?.data || error;
    }
  }

  // Get single financial valuation record by ID
  static async getFinancialValuation(id) {
    try {
      const response = await api.get(`/financial-valuations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching financial valuation:', error);
      throw error.response?.data || error;
    }
  }

  // Create new financial valuation record
  static async createFinancialValuation(valuationData) {
    try {
      const response = await api.post('/financial-valuations', valuationData);
      return response.data;
    } catch (error) {
      console.error('Error creating financial valuation:', error);
      throw error.response?.data || error;
    }
  }

  // Update financial valuation record
  static async updateFinancialValuation(id, valuationData) {
    try {
      const response = await api.put(`/financial-valuations/${id}`, valuationData);
      return response.data;
    } catch (error) {
      console.error('Error updating financial valuation:', error);
      throw error.response?.data || error;
    }
  }

  // Delete financial valuation record
  static async deleteFinancialValuation(id) {
    try {
      const response = await api.delete(`/financial-valuations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting financial valuation:', error);
      throw error.response?.data || error;
    }
  }

  // Get financial valuation statistics
  static async getFinancialStats() {
    try {
      const response = await api.get('/financial-valuations/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching financial stats:', error);
      throw error.response?.data || error;
    }
  }

  // Calculate depreciation for an asset
  static async calculateDepreciation(assetId, method, params = {}) {
    try {
      const response = await api.post(`/financial-valuations/${assetId}/calculate-depreciation`, {
        method,
        ...params
      });
      return response.data;
    } catch (error) {
      console.error('Error calculating depreciation:', error);
      throw error.response?.data || error;
    }
  }

  // Get depreciation schedule for an asset
  static async getDepreciationSchedule(assetId) {
    try {
      const response = await api.get(`/financial-valuations/${assetId}/depreciation-schedule`);
      return response.data;
    } catch (error) {
      console.error('Error fetching depreciation schedule:', error);
      throw error.response?.data || error;
    }
  }

  // Perform impairment test
  static async performImpairmentTest(assetId, testData) {
    try {
      const response = await api.post(`/financial-valuations/${assetId}/impairment-test`, testData);
      return response.data;
    } catch (error) {
      console.error('Error performing impairment test:', error);
      throw error.response?.data || error;
    }
  }

  // Get financial reports
  static async getFinancialReports(reportType, params = {}) {
    try {
      const response = await api.get(`/financial-valuations/reports/${reportType}`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching financial reports:', error);
      throw error.response?.data || error;
    }
  }

  // Export financial data
  static async exportFinancialData(format = 'excel', filters = {}) {
    try {
      const response = await api.post('/financial-valuations/export', {
        format,
        filters
      }, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting financial data:', error);
      throw error.response?.data || error;
    }
  }

  // Get assets for financial valuation
  static async getAssetsForValuation() {
    try {
      const response = await api.get('/assets', {
        params: { 
          status: 'Active',
          limit: 100 // Maximum allowed by validation
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching assets for valuation:', error);
      throw error.response?.data || error;
    }
  }

  // Search financial valuations
  static async searchFinancialValuations(searchTerm, filters = {}) {
    try {
      const response = await api.get('/financial-valuations/search', {
        params: { searchTerm, ...filters }
      });
      return response.data;
    } catch (error) {
      console.error('Error searching financial valuations:', error);
      throw error.response?.data || error;
    }
  }

  // Get valuation history for an asset
  static async getValuationHistory(assetId) {
    try {
      const response = await api.get(`/financial-valuations/${assetId}/history`);
      return response.data;
    } catch (error) {
      console.error('Error fetching valuation history:', error);
      throw error.response?.data || error;
    }
  }

  // Update asset valuation
  static async updateAssetValuation(assetId, valuationData) {
    try {
      const response = await api.put(`/financial-valuations/asset/${assetId}`, valuationData);
      return response.data;
    } catch (error) {
      console.error('Error updating asset valuation:', error);
      throw error.response?.data || error;
    }
  }
}

export default FinancialValuationService;
